import React, { useEffect, useRef, useState } from 'react';
import './IntroClock.css';

const AUCKLAND_TIME_ZONE = 'Pacific/Auckland';

export type AucklandClockSnapshot = {
  hour: number;
  minute: number;
  second: number;
  digitalTime: string;
  zone: string;
  isoTime: string;
};

const getPart = (
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes
) => parts.find((part) => part.type === type)?.value ?? '';

export const getAucklandClockSnapshot = (
  now: Date = new Date()
): AucklandClockSnapshot => {
  const timeParts = new Intl.DateTimeFormat('en-NZ', {
    timeZone: AUCKLAND_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const zoneParts = new Intl.DateTimeFormat('en-NZ', {
    timeZone: AUCKLAND_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  }).formatToParts(now);
  const hour = Number(getPart(timeParts, 'hour'));
  const minute = Number(getPart(timeParts, 'minute'));
  const second = Number(getPart(timeParts, 'second'));

  return {
    hour,
    minute,
    second,
    digitalTime: new Intl.DateTimeFormat('en-US', {
      timeZone: AUCKLAND_TIME_ZONE,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(now),
    zone: getPart(zoneParts, 'timeZoneName'),
    isoTime: `${String(hour).padStart(2, '0')}:${String(minute).padStart(
      2,
      '0'
    )}:${String(second).padStart(2, '0')}`,
  };
};

const drawClock = (
  canvas: HTMLCanvasElement,
  snapshot: AucklandClockSnapshot
) => {
  let context: CanvasRenderingContext2D | null = null;

  try {
    context = canvas.getContext('2d');
  } catch {
    return;
  }

  if (!context) {
    return;
  }

  const radius = canvas.width / 2;
  const secondAngle = (Math.PI / 30) * snapshot.second;
  const minuteAngle =
    (Math.PI / 30) * snapshot.minute +
    (Math.PI / 1800) * snapshot.second;
  const hourAngle =
    (Math.PI / 6) * (snapshot.hour % 12) +
    (Math.PI / 360) * snapshot.minute;

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.save();
  context.translate(radius, radius);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';

  context.strokeStyle = '#ffffff';
  context.lineWidth = 6;
  context.lineCap = 'butt';
  context.beginPath();
  context.moveTo(
    -Math.sin(hourAngle) * 16,
    Math.cos(hourAngle) * 16
  );
  context.lineTo(Math.sin(hourAngle) * 60, -Math.cos(hourAngle) * 60);
  context.stroke();

  context.strokeStyle = '#d1dd66';
  context.beginPath();
  context.moveTo(
    -Math.sin(minuteAngle) * 16,
    Math.cos(minuteAngle) * 16
  );
  context.lineTo(Math.sin(minuteAngle) * 60, -Math.cos(minuteAngle) * 60);
  context.stroke();

  context.fillStyle = '#e64b38';
  context.beginPath();
  context.arc(
    Math.sin(secondAngle) * (radius - 12),
    -Math.cos(secondAngle) * (radius - 12),
    5,
    0,
    2 * Math.PI
  );
  context.fill();
  context.restore();
};

export const IntroClock: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [snapshot, setSnapshot] = useState<AucklandClockSnapshot>(() =>
    getAucklandClockSnapshot()
  );

  useEffect(() => {
    const updateClock = () => {
      const nextSnapshot = getAucklandClockSnapshot();
      setSnapshot(nextSnapshot);

      if (canvasRef.current) {
        drawClock(canvasRef.current, nextSnapshot);
      }
    };

    updateClock();
    const intervalId = window.setInterval(updateClock, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <div
      className="intro-clock"
      role="group"
      aria-label={`Current time in Auckland: ${snapshot.digitalTime} ${snapshot.zone}`}
    >
      <canvas
        ref={canvasRef}
        className="intro-clock__face"
        width="160"
        height="160"
        aria-hidden="true"
      />
      <div className="intro-clock__details">
        <div>Auckland, NZ</div>
        <div className="intro-clock__digital">
          <time dateTime={snapshot.isoTime}>{snapshot.digitalTime}</time>
          <span>{snapshot.zone}</span>
        </div>
      </div>
    </div>
  );
};
